import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Eye, Loader2, Check, X, Minus } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pctClass = (p) => p == null ? 'text-slate-400' : p >= 70 ? 'text-emerald-700' : p >= 50 ? 'text-amber-700' : 'text-red-600';
const VAL = { A: ['A', 'bg-emerald-100 text-emerald-800'], ECA: ['ECA', 'bg-amber-100 text-amber-800'], NA: ['NA', 'bg-red-100 text-red-700'] };

const Cellule = ({ c, type }) => {
  if (!c || (c.correct == null && !c.valeur && c.pourcentage == null)) return <span className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-300"><Minus className="w-3 h-3" /></span>;
  if (type === 'evaluation') {
    const v = VAL[c.valeur];
    return <span className={`inline-flex items-center justify-center min-w-[2.5rem] h-6 px-1.5 rounded text-[10px] font-bold ${v ? v[1] : 'bg-slate-100 text-slate-600'}`} title={c.pourcentage != null ? `${c.pourcentage} %` : ''}>{v ? v[0] : c.pourcentage != null ? `${c.pourcentage} %` : '—'}</span>;
  }
  return <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${c.correct ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`} title={c.reponse ? `Réponse : ${c.reponse}` : ''}>{c.correct ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}</span>;
};

// Tableau : questions (ou savoir-faire) en lignes, candidats en colonnes
export const GrilleResultats = ({ data }) => {
  if (!data) return null;
  const { candidats, lignes, type } = data;
  if (candidats.length === 0) return <p className="text-sm text-slate-400 italic py-6 text-center" data-testid="grille-vide">Aucun passage enregistré{data.date_journee ? ' pour cette journée' : ''}.</p>;
  return (
    <div className="overflow-auto max-h-[70vh] rounded-lg border" data-testid="grille-resultats">
      <table className="text-xs border-collapse min-w-full">
        <thead className="sticky top-0 z-10 bg-slate-50">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-600 sticky left-0 bg-slate-50 min-w-[260px] border-b">{type === 'qcm' ? 'Question' : 'Savoir-faire / connaissance'}</th>
            {candidats.map(c => (
              <th key={c.id} className="px-2 py-2 border-b border-l text-center align-bottom min-w-[72px]" data-testid={`grille-col-${c.id}`}>
                <div className="font-semibold text-slate-800 leading-tight">{c.nom}</div>
                <div className="text-[10px] text-slate-500 font-normal leading-tight">{c.prenom}</div>
                <div className={`mt-1 text-[11px] font-bold ${pctClass(c.pourcentage)}`}>{c.pourcentage != null ? `${Math.round(c.pourcentage)} %` : '—'}</div>
                {type === 'qcm' && c.score != null && <div className="text-[9px] text-slate-400 font-normal">{c.score}/{c.score_max}</div>}
                {type === 'evaluation' && c.statut_global && <div className={`text-[9px] font-bold ${pctClass(c.statut_global === 'A' ? 100 : c.statut_global === 'ECA' ? 60 : 0)}`}>{c.statut_global}</div>}
                {c.tentative > 1 && <div className="text-[9px] text-indigo-500 font-normal">tentative {c.tentative}</div>}
              </th>
            ))}
            <th className="px-2 py-2 border-b border-l text-center text-slate-500 font-semibold min-w-[64px]">Réussite</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={l.id} className={i % 2 ? 'bg-slate-50/50' : 'bg-white'} data-testid={`grille-ligne-${l.id}`}>
              <td className="px-3 py-1.5 sticky left-0 bg-inherit border-r text-slate-700 align-top">
                <span className="text-slate-400 mr-1.5">{i + 1}.</span>{l.texte}
                {l.bonne_reponse && <span className="block text-[10px] text-emerald-700 mt-0.5">✔ {l.bonne_reponse}</span>}
              </td>
              {candidats.map(c => <td key={c.id} className="px-2 py-1.5 border-l text-center"><Cellule c={l.cellules[c.id]} type={type} /></td>)}
              <td className={`px-2 py-1.5 border-l text-center font-semibold ${pctClass(l.taux_reussite)}`}>{l.taux_reussite != null ? `${l.taux_reussite} %` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const GrilleResultatsDialog = ({ open, onClose, testType, testId, dossierId, dateJournee, titre }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !testId || !dossierId) return;
    setLoading(true); setData(null);
    const base = testType === 'qcm' ? 'qcms' : 'evaluations-manuelles';
    axios.get(`${API_URL}/${base}/${testId}/grille-dossier/${dossierId}`, { params: dateJournee ? { date_journee: dateJournee } : {} }).then(r => setData(r.data)).catch(() => setData({ candidats: [], lignes: [], type: testType })).finally(() => setLoading(false));
  }, [open, testType, testId, dossierId, dateJournee]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[95vw] w-fit min-w-[640px]" data-testid="grille-resultats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Eye className="w-4 h-4 text-indigo-600" /> Résultats — {data?.titre || titre}
            {dateJournee && <span className="text-xs font-normal text-slate-500">· journée du {dateJournee.split('-').reverse().join('/')}</span>}
            {data?.seuil != null && <span className="text-xs font-normal text-slate-500">· seuil {data.seuil} %</span>}
          </DialogTitle>
        </DialogHeader>
        {loading ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : <GrilleResultats data={data} />}
        {data?.type === 'qcm' && data.candidats?.length > 0 && <p className="text-[10px] text-slate-400">Survolez une case pour voir la réponse donnée. ✔ = bonne réponse attendue. Meilleure tentative retenue par candidat.</p>}
      </DialogContent>
    </Dialog>
  );
};

// Bouton œil autonome (ouvre la grille)
export const VoirResultatsButton = ({ testType, testId, dossierId, dateJournee, titre, className = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} className={`inline-flex items-center gap-1 p-1 rounded text-indigo-600 hover:bg-indigo-50 ${className}`} title="Voir les résultats (questions × candidats)" data-testid={`voir-resultats-${testType}-${testId}${dateJournee ? `-${dateJournee}` : ''}`}>
        <Eye className="w-3.5 h-3.5" />
      </button>
      <GrilleResultatsDialog open={open} onClose={() => setOpen(false)} testType={testType} testId={testId} dossierId={dossierId} dateJournee={dateJournee} titre={titre} />
    </>
  );
};
